import React, { useState, useEffect, useCallback, useRef } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Marca do Google (as quatro cores do "G")
const GoogleMark = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.4 14.4a7.2 7.2 0 0 1 0-4.6V6.7H1.4a12 12 0 0 0 0 10.8l4-3.1z" />
    <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.4 6.7l4 3.1C6.3 6.9 8.9 4.8 12 4.8z" />
  </svg>
);

const useStyles = makeStyles(theme => ({
  paper: { borderRadius: 16 },
  head: { display: "flex", alignItems: "flex-start", gap: 12, padding: "20px 20px 0" },
  iconChip: { width: 40, height: 40, borderRadius: 10, flexShrink: 0, background: "#EAF1FE", display: "flex", alignItems: "center", justifyContent: "center" },
  titleCol: { flex: 1, minWidth: 0 },
  name: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 16, color: theme.app.ink },
  subtitle: { fontSize: 12.5, color: theme.app.textTertiary, marginTop: 2 },
  pill: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: "3px 10px", marginTop: 8, whiteSpace: "nowrap" },
  pillOn: { background: "#EAF6EE", color: "#15784B" },
  pillOff: { background: "#FBF3E4", color: "#B7791F" },
  dot: { width: 6, height: 6, borderRadius: "50%", background: "currentColor" },
  closeBtn: { color: theme.app.textTertiary, padding: 5, marginTop: -4, marginRight: -4, "& svg": { fontSize: 19 } },

  intro: { fontSize: 13.5, color: theme.app.textSecondary, lineHeight: 1.55, margin: "16px 0 4px" },
  section: { marginTop: 20 },
  label: { fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", color: theme.app.textTertiary, marginBottom: 8 },
  hint: { fontSize: 12, color: theme.app.textTertiary, marginTop: 6, lineHeight: 1.5 },

  account: { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: theme.app.hover, border: `1px solid ${theme.app.border}`, borderRadius: 10 },
  accountEmail: { fontSize: 14, fontWeight: 600, color: theme.app.ink, wordBreak: "break-all" },

  warn: { marginTop: 14, padding: "10px 12px", borderRadius: 8, background: "#FBF3E4", color: "#B7791F", fontSize: 12.5, lineHeight: 1.5 },

  scopes: { margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 },
  scope: { fontSize: 12.5, color: theme.app.textSecondary, lineHeight: 1.45 },

  connectBtn: { textTransform: "none", fontWeight: 600, borderRadius: 8, height: 44, background: "#fff", border: `1px solid ${theme.app.border}`, color: theme.app.ink, "&:hover": { background: theme.app.lineSoft } },
  footer: { padding: "12px 20px 16px" },
  btn: { textTransform: "none", fontWeight: 600, borderRadius: 8 },
  btnPrimary: { background: theme.app.dark || "#2C353D", color: "#fff", boxShadow: "none", "&:hover": { background: "#1f262c", boxShadow: "none" } },
  danger: { color: theme.app.danger },
}));

// O que cada escopo permite, em português. A tela de consentimento do Google é
// genérica; aqui o usuário vê o que a Numera realmente vai fazer.
const SCOPE_LABELS = {
  "https://www.googleapis.com/auth/calendar.events": "Criar, ver e alterar compromissos nas suas agendas",
  "https://www.googleapis.com/auth/calendar.freebusy": "Ver quais horários estão ocupados (sem ver o conteúdo dos compromissos)",
  "https://www.googleapis.com/auth/calendar.calendarlist.readonly": "Ver a lista das suas agendas (para você escolher uma)",
  "https://www.googleapis.com/auth/calendar.app.created": "Gerenciar apenas as agendas e eventos criados pela Numera",
  email: "Ver o endereço de e-mail da conta conectada",
  openid: "Identificar a conta conectada",
};

const GoogleConnectorModal = ({ open, onClose, onSaved }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const popup = useRef(null);
  const poll = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/connectors/google/status");
      setInfo(data);
      return data;
    } catch (err) {
      toastError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCalendars = useCallback(async () => {
    try {
      const { data } = await api.get("/connectors/google/calendars");
      setCalendars(data.calendars || []);
    } catch {
      setCalendars([]); // sem agendas não quebra a tela; o select some
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    loadStatus().then(d => {
      if (d && d.connected) loadCalendars();
    });
  }, [open, loadStatus, loadCalendars]);

  const stopPolling = useCallback(() => {
    if (poll.current) {
      clearInterval(poll.current);
      poll.current = null;
    }
  }, []);

  // Conclusão da autorização. Três caminhos, do mais rápido ao mais teimoso:
  //
  // 1) BroadcastChannel — é o que vale. Liga documentos da MESMA origem sem
  //    depender de quem abriu quem, então sobrevive ao corte que o Google causa
  //    (COOP: same-origin em accounts.google.com zera o window.opener da janela
  //    de forma permanente). Chega no instante em que o popup volta.
  // 2) postMessage no opener — atalho de quando o vínculo por acaso sobrevive.
  //    Na prática, com o Google, não sobrevive.
  // 3) sondagem do /status — rede de segurança para navegador sem
  //    BroadcastChannel, ou popup fechado no meio do caminho.
  const finish = useCallback(
    async email => {
      stopPolling();
      setBusy(false);
      toast.success(`Conta ${email || "Google"} conectada.`);
      const s = await loadStatus();
      if (s && s.connected) await loadCalendars();
      onSaved && onSaved();
    },
    [stopPolling, loadStatus, loadCalendars, onSaved]
  );

  useEffect(() => {
    if (!open) return undefined;

    const handle = d => {
      if (!d || d.source !== "numera-google") return;
      if (!d.ok) {
        stopPolling();
        setBusy(false);
        toast.error(d.error || "Não foi possível conectar a conta Google.");
        return;
      }
      finish(d.email);
    };

    let ch = null;
    try {
      ch = new BroadcastChannel("numera-google");
      ch.onmessage = ev => handle(ev.data);
    } catch {
      /* navegador sem BroadcastChannel: sobra a sondagem */
    }

    const onMessage = ev => {
      if (ev.origin !== window.location.origin) return;
      handle(ev.data);
    };
    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("message", onMessage);
      if (ch) ch.close();
    };
  }, [open, finish, stopPolling]);

  // para a sondagem ao fechar o modal / desmontar
  useEffect(() => stopPolling, [stopPolling]);
  useEffect(() => {
    if (!open) stopPolling();
  }, [open, stopPolling]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connectors/google/start", {});
      // centraliza na tela ONDE o studio está: screenLeft/screenTop cobrem o
      // caso de dois monitores (sem eles a janela abre sempre no monitor
      // principal, no canto). innerWidth/Height = área útil da janela atual.
      const w = 520;
      const h = 680;
      const sx = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
      const sy = window.screenTop !== undefined ? window.screenTop : window.screenY;
      const vw = window.innerWidth || document.documentElement.clientWidth || window.screen.width;
      const vh = window.innerHeight || document.documentElement.clientHeight || window.screen.height;
      const left = Math.max(0, Math.round(sx + (vw - w) / 2));
      const top = Math.max(0, Math.round(sy + (vh - h) / 2));
      popup.current = window.open(
        data.authUrl,
        "numera-google-oauth",
        `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no`
      );
      if (!popup.current) {
        setBusy(false);
        toast.error("O navegador bloqueou a janela. Libere pop-ups para este site e tente de novo.");
        return;
      }
      // sonda o status enquanto o usuário autoriza (ver comentário em `finish`).
      // Desiste em 3 min para não sondar para sempre se a janela for abandonada.
      stopPolling();
      const until = Date.now() + 3 * 60 * 1000;
      poll.current = setInterval(async () => {
        if (Date.now() > until) {
          stopPolling();
          setBusy(false);
          return;
        }
        try {
          const { data: s } = await api.get("/connectors/google/status");
          if (s && s.connected && !s.needsReconnect) finish(s.email);
        } catch {
          /* rede instável: tenta de novo no próximo tick */
        }
      }, 2000);
    } catch (err) {
      setBusy(false);
      toastError(err);
    }
  };

  const pickCalendar = async calendarId => {
    setBusy(true);
    try {
      await api.put("/connectors/google/calendar", { calendarId });
      setInfo(p => ({ ...p, calendarId }));
      onSaved && onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/connectors/google/disconnect", {});
      setCalendars([]);
      await loadStatus();
      onSaved && onSaved();
      toast.success("Conta Google desconectada.");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const connected = !!(info && info.connected);
  const needsReconnect = !!(info && info.needsReconnect);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: classes.paper }}>
      <div className={classes.head}>
        <div className={classes.iconChip}><GoogleMark /></div>
        <div className={classes.titleCol}>
          <div className={classes.name}>Google Agenda</div>
          <div className={classes.subtitle}>Consultar horários, marcar e cancelar compromissos.</div>
          <span className={`${classes.pill} ${connected && !needsReconnect ? classes.pillOn : classes.pillOff}`}>
            <span className={classes.dot} />
            {needsReconnect ? "Precisa reconectar" : connected ? "Conectado" : "Não conectado"}
          </span>
        </div>
        <IconButton className={classes.closeBtn} onClick={onClose}><CloseIcon /></IconButton>
      </div>

      <DialogContent>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}><CircularProgress size={26} /></div>
        ) : (
          <>
            <div className={classes.intro}>
              Ligue a agenda do Google e os fluxos passam a consultar disponibilidade e marcar
              compromissos direto nela — inclusive durante uma ligação com o agente de voz.
            </div>

            {info && !info.configured && (
              <div className={classes.warn}>
                A integração com o Google ainda não foi configurada neste servidor
                (<b>GOOGLE_CLIENT_ID</b> / <b>GOOGLE_CLIENT_SECRET</b>). Fale com o suporte.
              </div>
            )}

            {needsReconnect && (
              <div className={classes.warn}>
                A autorização expirou ou foi revogada no Google. Reconecte para os fluxos
                voltarem a funcionar.
              </div>
            )}

            {connected && (
              <div className={classes.section}>
                <div className={classes.label}>Conta conectada</div>
                <div className={classes.account}>
                  <GoogleMark size={18} />
                  <span className={classes.accountEmail}>{info.email || "conta Google"}</span>
                </div>
              </div>
            )}

            {/* A agenda usada é escolhida em cada NÓ do fluxo (lista), não aqui. */}

            <div className={classes.section}>
              <div className={classes.label}>O que a Numera vai poder fazer</div>
              <ul className={classes.scopes}>
                {((info && info.scopes) || []).map(s => (
                  <li key={s} className={classes.scope}>• {SCOPE_LABELS[s] || s}</li>
                ))}
              </ul>
              <div className={classes.hint}>
                Você pode revogar quando quiser, aqui ou em myaccount.google.com/permissions.
              </div>
            </div>

            {!connected && (
              <div className={classes.section}>
                <Button
                  fullWidth
                  className={classes.connectBtn}
                  startIcon={<GoogleMark size={18} />}
                  onClick={connect}
                  disabled={busy || !(info && info.configured)}
                >
                  {busy ? "Aguardando o Google…" : "Conectar com Google"}
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.footer}>
        {connected && (
          <Button className={`${classes.btn} ${classes.danger}`} onClick={disconnect} disabled={busy}>
            Desconectar
          </Button>
        )}
        {connected && needsReconnect && (
          <Button className={classes.btn} onClick={connect} disabled={busy}>
            Reconectar
          </Button>
        )}
        <div style={{ flex: 1 }} />
        <Button className={`${classes.btn} ${classes.btnPrimary}`} variant="contained" onClick={onClose}>
          Concluir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoogleConnectorModal;
